import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

const REFERRAL_PARAMS_KEY = "sf_referral_params";

interface ReferralParams {
  utm: {
    campaign: string;
    content: string;
    medium: string;
    source: string;
    term: string;
  };
  ref?: string;
  timestamp: number;
}

function getUTMParams(): ReferralParams | null {
  const stored = localStorage.getItem(REFERRAL_PARAMS_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  return null;
}

function saveUTMParams(params: ReferralParams) {
  localStorage.setItem(REFERRAL_PARAMS_KEY, JSON.stringify(params));
}

export function captureReferralParams() {
  // Don't capture if we already have stored params
  if (getUTMParams()) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);

  const params: ReferralParams = {
    utm: {
      campaign: "",
      content: "",
      medium: "",
      source: "",
      term: "",
    },
    timestamp: Date.now(),
  };

  // Capture ref parameter separately
  const refValue = urlParams.get("ref");
  if (refValue) {
    params.ref = refValue;
    urlParams.delete("ref");
  }

  // Capture all UTM parameters
  const utmParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ] as const;
  let hasParams = false;

  utmParams.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      // Remove 'utm_' prefix and assign to the correct field in utm object
      const utmField = param.replace("utm_", "") as keyof typeof params.utm;
      params.utm[utmField] = value;
      hasParams = true;
      urlParams.delete(param);
    }
  });

  // Only save if we found any parameters
  if (hasParams) {
    saveUTMParams(params);

    // Clean up URL
    const newUrl =
      window.location.pathname +
      (urlParams.toString() ? `?${urlParams.toString()}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }
}

export async function handleReferralCode(uid: string) {
  const params = getUTMParams();
  if (!params) {
    return;
  }

  // Update Firebase user document with referral information
  const userRef = doc(db, "users", uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const userData = userDoc.data();
    if (!userData.referral) {
      await updateDoc(userRef, {
        referral: params.ref || params.utm.source,
        utm: {
          ...params.utm,
          first_visit_timestamp: params.timestamp,
          registration_timestamp: Date.now(),
        },
      });

      // Clear stored params after successful update
      localStorage.removeItem(REFERRAL_PARAMS_KEY);
    }
  }
}
